import { getFinishedPercent } from 'chameleon-src/components/learnosity/utils/helpers';
import { getFormIdField, getSectionStudents } from './index';

/**
 * This function is currently used on DataWrapper to create lists based on assignments and sections.
 * @ returns an object with 5 lists:
 *  - sectionsNotAssigned: sections that dont have any assignments on this assessment.
 *  - reassignSections: sections that can be reassigned to same assessment;
 *  - inProgressSections: sections with in progress assignment of assessment;
 *  - preassignedSections: preassigned sections behavior ?? lacks documentation;
 *  - auxDisabledStudents: students with individual assignments;
 */
const getFilteredSectionsLists = ({
  assignments,
  groups,
  individualStudentAssignmentFlag,
  isFinalExam,
  subjectClasses,
  hasPreassignedBehavior,
}) => {
  // map a list os group with its assignments and usaStudents when applicable
  const groupAssignmentsList = groups.map((g) => {
    const groupId = g.id.toString();

    // all assignments of the group, regardless if it is or individual assignment
    const associatedAssignments = assignments
      .filter((a) => a.assignees[0].id === g.id)
      .map((a) => ({
        ...a,
        isAssignmentComplete: getFinishedPercent(a.progress) === 100,
      }));

    // check if its an individual assignment
    const withIndividualAssignments =
      !isFinalExam && individualStudentAssignmentFlag;
    const uncompletedISAs = associatedAssignments.filter(
      ({
        progress,
        status,
        is_individual_assignment: isIndividualAssignment,
      }) => {
        const finishedPercentage = getFinishedPercent(progress);

        return (
          status !== 'closed' &&
          finishedPercentage !== 100 &&
          isIndividualAssignment
        );
      },
    );
    // define if its a case of having individual assignments
    const isIndividualAssignments =
      withIndividualAssignments && uncompletedISAs.length;

    // get all section assignments, not individual
    const sectionAssignments = associatedAssignments.filter(
      ({ is_individual_assignment: isIndividualAssignment }) =>
        !isIndividualAssignment,
    );

    return {
      group: g,
      assignments: associatedAssignments,
      sectionAssignments,
      sectionStudents: getSectionStudents(subjectClasses, groupId),
      individualAssignments: isIndividualAssignments
        ? {
            uncompletedISAs,
            isaStudents:
              isIndividualAssignments &&
              uncompletedISAs.reduce((acc, isa) => {
                const { students: currentIsaStudents } = isa;

                return acc.concat(
                  isa.sub_group_students.map((student) => {
                    const studentId = student.id.toString();

                    // Do not use ISA's ID here because we don't have multiple ISAs within Assign To sections.
                    return {
                      ...student,
                      id: getFormIdField(studentId, groupId),
                      studentId,
                      sectionId: groupId,
                      status: (
                        currentIsaStudents.find(
                          (isaStudent) =>
                            isaStudent.id.toString() === studentId,
                        ) || {}
                      ).status,
                    };
                  }),
                );
              }, []),
          }
        : null,
    };
  });

  const getPreassignments = (assignments2filter) => {
    if (!hasPreassignedBehavior) return [];

    return assignments2filter.filter(
      ({ teacher_assigned: teacherAssigned }) => !teacherAssigned,
    );
  };

  /**
   * Filter sections that can be assigned, one of those conditions:
   * - is individual assignment and there are still students on section not assigned yet
   * - is not individual assignmend and:
   *   -- has no non-individual assignments
   *   -- has no preassigned behavior or there are assignments ???
   *
   * @param {group, individualAssignments, sectionAssignments } - Group section, list of individual assignments and list of section assignments.
   * @param {bool} isFinalExam - if assessment is final exam or not
   */
  const filterNotAssignedSections = ({
    individualAssignments,
    sectionAssignments,
    sectionStudents,
  }) => {
    if (individualAssignments) {
      const { isaStudents } = individualAssignments;
      return isaStudents.length < sectionStudents.length;
    }

    const preassignedAssignments = getPreassignments(sectionAssignments);

    return (
      sectionAssignments.length === 0 && preassignedAssignments.length === 0
    );
  };

  /**
   * Filter groups if are not individual assignments and all assignments are:
   * completed or with all submissions
   * except if its a final exam, which cant be reassigned
   *
   * @param {group, individualAssignments, sectionAssignments } - Group section, list of individual assignments and list of section assignments.
   * @param {bool} isFinalExam - if assessment is final exam or not
   */
  const filterReassignedSections = ({
    individualAssignments,
    sectionAssignments,
  }) => {
    if (individualAssignments || isFinalExam) return false;

    return (
      sectionAssignments.length &&
      sectionAssignments.every(
        (a) => a.isAssignmentComplete || a.status === 'closed',
      )
    );
  };

  /**
   * reduce function to add to inProgressSections the group with assignment if
   * it is still in progress.
   * conditions to be inprogress:
   * - is individualAssignments and there is incompleted isa students;
   * - there is an incomplete assginment
   *
   * @param {Array} inProgressSections - list of in progress sections.
   * @param {group, individualAssignments, sectionAssignments } - Group section, list of individual assignments and list of section assignments.
   * @param {bool} isFinalExam - if assessment is final exam or not
   */
  const reduceInProgressSections = (
    inProgressSections,
    { group, individualAssignments, sectionAssignments },
  ) => {
    if (individualAssignments) {
      const { uncompletedISAs } = individualAssignments;
      return inProgressSections.concat(
        uncompletedISAs.map((isa) => ({
          ...group,
          assignmentId: isa.id,
          isIndividualAssignment: isa.is_individual_assignment,
          subGroupStudents: isa.sub_group_students,
        })),
      );
    }

    const orderedAssignments = [...sectionAssignments].sort(
      (a, b) => a.id - b.id,
    );
    const incompleteAssignment = orderedAssignments.find(
      (a) =>
        isFinalExam ||
        (!a.isAssignmentComplete &&
          a.status !== 'closed' &&
          (!hasPreassignedBehavior || a.teacher_assigned)),
    );

    if (incompleteAssignment) {
      return inProgressSections.concat([
        {
          ...group,
          assignmentId: incompleteAssignment.id,
        },
      ]);
    }

    return inProgressSections;
  };

  /**
   * Reduce a list of preassigned sections.
   * The condition to be match is:
   * - is the only incomplete assignments ?? what if has more than 1?
   * - and the teach assigned is falsy
   */
  const reducePreassignedSections = (
    preassignedSections,
    { group, sectionAssignments, individualAssignments },
  ) => {
    if (!hasPreassignedBehavior) return preassignedSections;

    const hasUncompletedISAs =
      ((individualAssignments || {}).uncompletedISAs || []).length > 0;
    if (hasUncompletedISAs) return preassignedSections;

    const incompleteAssignments = sectionAssignments.filter(
      (a) => isFinalExam || (!a.isAssignmentComplete && a.status !== 'closed'),
    );

    if (incompleteAssignments.length !== 1) return preassignedSections;

    const [incompleteAssignment] = incompleteAssignments;
    const { teacher_assigned: teacherAssigned } = incompleteAssignment;
    const assignmentId = incompleteAssignment.id.toString();

    const isPreassigned = !teacherAssigned;

    if (isPreassigned) {
      return preassignedSections.concat([
        {
          ...group,
          id: group.id.toString(),
          assignmentId,
        },
      ]);
    }

    return preassignedSections;
  };

  /**
   * Reduce a list of preassigned sections.
   * The condition to be match is:
   * - is the only incomplete assignments
   * - and the teach assigned is falsy
   */
  const reduceAuxDisabledStudents = (
    auxDisabledStudents,
    { individualAssignments, sectionStudents },
  ) => {
    if (!individualAssignments) return auxDisabledStudents;

    const { isaStudents } = individualAssignments;
    if (isaStudents.length < sectionStudents.length) {
      return auxDisabledStudents.concat(isaStudents);
    }
    return auxDisabledStudents;
  };

  // list of sections not assigned
  const sectionsNotAssigned = groupAssignmentsList
    .filter((ga) => filterNotAssignedSections(ga))
    .map(({ group }) => group);
  // list of section that can be reassigned
  const reassignSections = groupAssignmentsList
    .filter((ga) => filterReassignedSections(ga))
    .map(({ group }) => group);
  // list of in progress sections
  const inProgressSections = groupAssignmentsList.reduce(
    (acc, ga) => reduceInProgressSections(acc, ga),
    [],
  );
  // list of preassigned sections
  const preassignedSections = groupAssignmentsList.reduce(
    (acc, ga) => reducePreassignedSections(acc, ga),
    [],
  );
  // list of aux disable students
  const auxDisabledStudents = groupAssignmentsList.reduce(
    (acc, ga) => reduceAuxDisabledStudents(acc, ga),
    [],
  );

  return {
    sectionsNotAssigned,
    preassignedSections,
    inProgressSections,
    reassignSections,
    auxDisabledStudents,
  };
};

export default getFilteredSectionsLists;
